import { useId } from "react";
import type { ReactNode } from "react";
import { cx } from "./cx";
import styles from "./Field.module.css";

/** Ce que `Field` passe à sa commande. Tout doit être reporté sur l'élément. */
export type FieldControlProps = {
  /** À poser sur `id` de la commande : c'est ce qui lie le `<label>`. */
  id: string;
  /** À poser sur `aria-describedby` : rattache la mention et l'erreur. */
  "aria-describedby": string | undefined;
  /** À poser sur `aria-invalid`. */
  "aria-invalid": boolean | undefined;
  /** À poser sur `required`. */
  required: boolean | undefined;
  /** Classe d'habillage commune à `input`, `select` et `textarea`. */
  className: string;
};

export type FieldProps = {
  /** Libellé, réellement associé à la commande par `htmlFor`/`id`. */
  label: ReactNode;
  /** Précision affichée sous la commande, et annoncée avec elle. */
  hint?: ReactNode;
  /**
   * Message d'erreur. Non vide, il colore la commande, pose `aria-invalid` et
   * est annoncé — un champ rouge sans texte n'apprend rien à personne.
   */
  error?: ReactNode;
  /** Marque le champ obligatoire, visuellement et pour l'assistance technique. */
  required?: boolean;
  /**
   * La commande, sous forme de fonction : `Field` fabrique l'identifiant et le
   * passe. Une fonction et non un nœud, parce que c'est le seul moyen de
   * **garantir** l'association libellé ↔ commande — la première chose qu'un
   * audit d'accessibilité vérifie, et la première qu'on oublie.
   */
  children: (props: FieldControlProps) => ReactNode;
  className?: string;
};

/**
 * Enveloppe d'un champ de formulaire : libellé, commande, mention, erreur.
 *
 * ```tsx
 * <Field label="Durée estimée" hint="En jours" error={erreurJours} required>
 *   {(commande) => (
 *     <input {...commande} type="number" min={1} value={jours}
 *            onChange={(e) => setJours(e.target.value)} />
 *   )}
 * </Field>
 * ```
 */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: FieldProps) {
  const id = useId();
  const idMention = `${id}-mention`;
  const idErreur = `${id}-erreur`;

  const decrit = [hint !== undefined ? idMention : null, error ? idErreur : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cx(styles.champ, className)}>
      <label className={styles.libelle} htmlFor={id}>
        {label}
        {required && (
          <span className={styles.requis} aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children({
        id,
        "aria-describedby": decrit || undefined,
        "aria-invalid": error ? true : undefined,
        required: required || undefined,
        className: styles.commande,
      })}

      {hint !== undefined && (
        <p id={idMention} className={styles.mention}>
          {hint}
        </p>
      )}

      {error && (
        // `role="alert"` : l'erreur est annoncée dès qu'elle apparaît, sans
        // attendre que l'utilisateur revienne sur le champ.
        <p id={idErreur} className={styles.erreur} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
